// src/components/outline/OutlineEditorModal.jsx
// Post-Ingestion Outline Editor Modal - Scrollable overlay on Title page
// Supports: title editing, text editing, reorder, progress tracking, completion

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Save, Loader2, Check, AlertCircle, X,
  Edit3, ChevronUp, ChevronDown, RefreshCw, Copy, AlertTriangle
} from 'lucide-react';
import { useNotification } from '@/contexts/NotificationContext';
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';

const API = `${BACKEND_URL}/api`;

// =============================================================================
// SEGMENT CARD COMPONENT
// =============================================================================
const SegmentCard = ({ 
  segment, 
  index, 
  totalCount,
  onTitleChange, 
  onTextChange,
  onMoveUp, 
  onMoveDown,
  isEditing,
  onEditToggle,
  disabled
}) => {
  return (
    <div className="border border-slate-600 bg-slate-800 rounded-lg p-4 hover:border-purple-400 transition-colors">
      <div className="flex items-start gap-3">
        {/* Order + Reorder */}
        <div className="flex flex-col items-center gap-1 pt-1">
          <span className="text-xs font-bold text-purple-400 bg-purple-900/50 px-2 py-0.5 rounded">
            #{index + 1}
          </span>
          <div className="flex flex-col gap-0.5 mt-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-5 w-5 p-0 text-slate-400 hover:text-white"
              onClick={() => onMoveUp(index)}
              disabled={index === 0 || disabled}
            >
              <ChevronUp className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 w-5 p-0 text-slate-400 hover:text-white"
              onClick={() => onMoveDown(index)}
              disabled={index === totalCount - 1 || disabled}
            >
              <ChevronDown className="w-3 h-3" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title Input */}
          <Input
            value={segment.title || ''}
            onChange={(e) => onTitleChange(segment.segment_id, e.target.value)}
            className="bg-slate-700 border-slate-600 text-white font-medium text-sm h-8"
            placeholder="Segment title..."
            disabled={disabled}
          />
          
          {/* Stats */}
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-2">
            <span>{segment.word_count?.toLocaleString() || 0} words</span>
            <span>·</span>
            <span className="font-mono text-slate-500">
              {segment.text_hash?.substring(0, 8)}...
            </span>
          </div>
          
          {/* Text Editor (collapsible) */}
          {isEditing && (
            <div className="mt-3">
              <Textarea
                value={segment.text || ''}
                onChange={(e) => onTextChange(segment.segment_id, e.target.value)}
                className="bg-slate-700 border-slate-600 text-white min-h-[150px] font-mono text-sm"
                placeholder="Segment text..."
                disabled={disabled}
              />
            </div>
          )}
        </div>
        
        {/* Edit Text Button */}
        <Button
          size="sm"
          variant={isEditing ? "default" : "outline"}
          className={`text-xs ${isEditing ? "bg-purple-600 hover:bg-purple-500" : "border-slate-600"}`}
          onClick={() => onEditToggle(segment.segment_id)}
          disabled={disabled}
        >
          <Edit3 className="w-3 h-3 mr-1" />
          {isEditing ? 'Close' : 'Edit'}
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// MODAL A: REGENERATING AUDIO (TRUTHFUL PROGRESS)
// =============================================================================
const RegeneratingModal = ({ job, stalledWarning }) => {
  // Detect contradiction
  const hasContradiction = (job?.dirty_segments_total > 0) && job?.skip_tts === true;
  
  // Calculate time since last update
  const [timeSinceUpdate, setTimeSinceUpdate] = useState(0);
  
  useEffect(() => {
    if (!job?.updated_at) return;
    
    const updateTimer = setInterval(() => {
      const updated = new Date(job.updated_at);
      const now = new Date();
      const diff = Math.floor((now - updated) / 1000);
      setTimeSinceUpdate(diff);
    }, 1000);
    
    return () => clearInterval(updateTimer);
  }, [job?.updated_at]);
  
  // Stage label mapping
  const stageLabels = {
    'GATE_4_MANUSCRIPT': 'Preparing manuscript',
    'GATE_5_TTS': 'Generating audio',
    'GATE_6_MANIFEST': 'Building manifest',
    'GATE_7_VALIDATE_PUBLISH': 'Verifying & publishing'
  };
  
  // Action label mapping
  const actionLabels = {
    'queued': 'Waiting to start...',
    'preparing_manifest': 'Preparing manifest...',
    'writing_text': 'Writing text to storage...',
    'tts_starting': 'Starting audio generation...',
    'tts_generating': 'Generating audio...',
    'tts_uploading': 'Uploading audio...',
    'manifest_writing': 'Writing manifest...',
    'verifying_assets': 'Verifying all audio exists...',
    'flipping_pointer': 'Publishing changes...',
    'skip_tts': 'No audio regeneration needed',
    'complete': 'Complete!'
  };
  
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-4">
      {hasContradiction && (
        <div className="bg-red-900/30 border border-red-500 rounded p-2 mb-4">
          <div className="flex items-center gap-2 text-red-400 font-medium text-sm">
            <AlertTriangle className="w-4 h-4" />
            CONTRADICTION DETECTED
          </div>
          <p className="text-red-300 text-xs mt-1">
            {job?.dirty_segments_total} dirty segments exist but skip_tts=true
          </p>
        </div>
      )}
      
      {stalledWarning && (
        <div className="bg-yellow-900/30 border border-yellow-500 rounded p-2 mb-4">
          <div className="flex items-center gap-2 text-yellow-400 font-medium text-sm">
            <AlertTriangle className="w-4 h-4" />
            Processing may be stalled
          </div>
          <p className="text-yellow-300 text-xs mt-1">
            No progress update for {timeSinceUpdate}s
          </p>
        </div>
      )}
      
      {/* Title */}
      <div className="flex items-center gap-2 mb-2">
        <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
        <h3 className="font-semibold text-white">Regenerating audio...</h3>
      </div>
      
      {/* Current segment (TRUTHFUL - from server) */}
      {job?.current_segment_title && (
        <p className="text-sm text-slate-300 mb-3">
          Now regenerating: <span className="text-white font-medium">{job.current_segment_title}</span>
        </p>
      )}
      
      {/* Counters */}
      {job?.dirty_segments_total > 0 && !job?.skip_tts && (
        <div className="mb-3">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-400">
              Segment {(job?.dirty_segments_done || 0) + 1} of {job?.dirty_segments_total}
            </span>
            <span className="text-slate-400">
              {Math.round(((job?.dirty_segments_done || 0) / job?.dirty_segments_total) * 100)}%
            </span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div 
              className="bg-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((job?.dirty_segments_done || 0) / job?.dirty_segments_total) * 100}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Stage label */}
      <p className="text-xs text-slate-400">
        Stage: {stageLabels[job?.stage] || job?.stage}
      </p>
      <p className="text-xs text-slate-500">
        {actionLabels[job?.current_action] || job?.current_action}
      </p>
      
      {/* Last update time */}
      <p className="text-xs text-slate-500 mt-2">
        Updated {timeSinceUpdate}s ago
      </p>
      
      {/* Details toggle (dev info) */}
      <details className="mt-4 pt-3 border-t border-slate-700">
        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">
          Debug details
        </summary>
        <div className="mt-2 text-xs font-mono space-y-1 text-slate-500">
          <div>job_id: {job?.job_id?.substring(0, 12)}...</div>
          <div>revision_id: {job?.revision_id?.substring(0, 12)}...</div>
          <div>dirty_segment_ids: [{job?.dirty_segment_ids?.slice(0, 3).join(', ')}]</div>
          <div>current_active_manifest: {job?.proof?.current_active_manifest_key?.substring(0, 30) || 'N/A'}...</div>
          <div>candidate_manifest: {job?.proof?.candidate_manifest_key?.substring(0, 30) || 'N/A'}...</div>
          <div>missing_audio_count: {job?.proof?.missing_audio_keys_count || 0}</div>
        </div>
      </details>
    </div>
  );
};

// =============================================================================
// MODAL B: COMPLETION (BELIEVABLE - ONLY AFTER VERIFICATION)
// =============================================================================
const CompletionModal = ({ isOpen, onClose, job, onPreview, playbackReady }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-400">
            <Check className="w-5 h-5" />
            Update complete
          </DialogTitle>
          <DialogDescription className="text-slate-300 pt-3 space-y-2">
            {job?.dirty_segments_total > 0 && !job?.skip_tts ? (
              <p>Updated {job.dirty_segments_total} segment(s) with new audio.</p>
            ) : (
              <p>Outline updated. No audio regeneration was needed.</p>
            )}
            
            {/* Playback readiness */}
            <div className="flex items-center gap-2 text-sm">
              {playbackReady?.ready ? (
                <>
                  <Check className="w-4 h-4 text-green-400" />
                  <span className="text-green-400">Playback verified: READY</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400">Playback check: {playbackReady?.reason || 'pending'}</span>
                </>
              )}
            </div>
            
            {/* Proof */}
            {job?.proof?.pointer_flipped && (
              <p className="text-xs text-slate-500">
                ✓ Manifest pointer flipped successfully
              </p>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2">
          <Button 
            onClick={onPreview}
            className="bg-purple-600 hover:bg-purple-700"
          >
            Preview changes
          </Button>
          <Button variant="outline" onClick={onClose} className="border-slate-600">
            Back to title
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// MODAL C: FAILURE (ACTIONABLE)
// =============================================================================
const FailureModal = ({ isOpen, onClose, job, onRetry }) => {
  const [showProof, setShowProof] = useState(false);
  
  const copyDebugInfo = () => {
    const info = JSON.stringify({
      job_id: job?.job_id,
      revision_id: job?.revision_id,
      stage: job?.stage,
      error: job?.fail_reason || job?.error,
      proof: job?.proof,
      timestamp: new Date().toISOString()
    }, null, 2);
    navigator.clipboard.writeText(info);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <AlertCircle className="w-5 h-5" />
            Update failed
          </DialogTitle>
          <DialogDescription className="text-slate-300 pt-3 space-y-3">
            {/* Error message */}
            <p className="text-red-300">
              {job?.fail_reason || job?.error || 'Unknown error occurred'}
            </p>
            
            {/* Stage where it failed */}
            <p className="text-xs text-slate-400">
              Failed at: {job?.stage}
            </p>
            
            {/* Missing audio count */}
            {job?.proof?.missing_audio_keys_count > 0 && (
              <p className="text-xs text-yellow-400">
                Missing audio files: {job.proof.missing_audio_keys_count}
              </p>
            )}
            
            {/* View proof toggle */}
            {showProof && (
              <pre className="text-xs bg-slate-900 p-2 rounded overflow-auto max-h-40">
                {JSON.stringify(job?.proof || {}, null, 2)}
              </pre>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowProof(!showProof)} 
            className="border-slate-600"
          >
            {showProof ? 'Hide proof' : 'View proof'}
          </Button>
          <Button variant="outline" onClick={copyDebugInfo} className="border-slate-600">
            <Copy className="w-4 h-4 mr-1" />
            Copy
          </Button>
          {onRetry && (
            <Button onClick={onRetry} className="bg-purple-600 hover:bg-purple-700">
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          )}
          <Button onClick={onClose} className="bg-slate-700">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// MAIN OUTLINE EDITOR MODAL
// =============================================================================
const OutlineEditorModal = ({ 
  isOpen, 
  onClose, 
  manuscriptId,
  onComplete  // Callback to refresh parent data
}) => {
  const { notifyError, notifySuccess } = useNotification();
  
  // State
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [segments, setSegments] = useState([]);
  const [originalSegments, setOriginalSegments] = useState([]);
  const [editingSegmentId, setEditingSegmentId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [failureError, setFailureError] = useState(null);
  const [stalledWarning, setStalledWarning] = useState(false);
  
  // Playback readiness state
  const [playbackReady, setPlaybackReady] = useState(null);
  
  // Load outline when modal opens
  useEffect(() => {
    if (isOpen && manuscriptId) {
      loadOutline();
    }
  }, [isOpen, manuscriptId]);
  
  // Poll for job progress with TRUTHFUL UI contract
  useEffect(() => {
    if (!isProcessing || !currentJob?.job_id) return;
    
    let pollCount = 0;
    const maxPollsBeforeWarning = 15; // 30 seconds at 2s intervals
    const maxPollTimeout = 180; // 3 minutes hard timeout
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      // Hard timeout check
      if (pollCount > maxPollTimeout / 2) {
        clearInterval(pollInterval);
        setIsProcessing(false);
        setStalledWarning(false);
        setFailureError(`Processing timed out after ${maxPollTimeout}s. Job may still be running in background.`);
        setShowFailure(true);
        return;
      }
      
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${API}/creator/titles/${manuscriptId}/outline/progress?job_id=${currentJob.job_id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const job = response.data;
        // Log progress for debugging
        console.log('[OutlineEditor] Progress:', job.state, job.stage, job.current_segment_title);
        
        setCurrentJob(prev => ({
          ...prev,
          ...job
        }));
        
        // Check for stalled state
        if (pollCount > maxPollsBeforeWarning && job.state === 'QUEUED') {
          setStalledWarning(true);
        }
        
        // TRUTHFUL state checking
        if (job.state === 'DONE') {
          clearInterval(pollInterval);
          setStalledWarning(false);
          
          // VERIFICATION: Call playback/ready before showing completion
          console.log('[OutlineEditor] Job done, verifying playback readiness...');
          try {
            const readyResponse = await axios.get(
              `${API}/manuscripts/${manuscriptId}/playback/ready`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setPlaybackReady(readyResponse.data);
            
            if (readyResponse.data.ready) {
              console.log('[OutlineEditor] Playback verified READY');
              setIsProcessing(false);
              setShowCompletion(true);
            } else {
              // Playback not ready - show failure with reason
              console.warn('[OutlineEditor] Playback NOT ready:', readyResponse.data.reason);
              setIsProcessing(false);
              setCurrentJob(prev => ({
                ...prev,
                fail_reason: `Playback not ready: ${readyResponse.data.reason}`
              }));
              setShowFailure(true);
            }
          } catch (readyErr) {
            console.error('[OutlineEditor] Playback check failed:', readyErr);
            // Still show completion but with warning
            setPlaybackReady({ ready: false, reason: 'Check failed' });
            setIsProcessing(false);
            setShowCompletion(true);
          }
        } else if (job.state === 'FAIL') {
          clearInterval(pollInterval);
          setIsProcessing(false);
          setStalledWarning(false);
          setShowFailure(true);
          console.error('[OutlineEditor] Job failed:', job.fail_reason);
        }
      } catch (error) {
        console.error('[OutlineEditor] Progress poll error:', error);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [isProcessing, currentJob?.job_id, manuscriptId]);
  
  const loadOutline = async () => {
    setLoading(true);
    setLoadError(null);
    
    try {
      const token = localStorage.getItem('accessToken');
      console.log('[OutlineEditor] Loading outline for:', manuscriptId);
      
      const response = await axios.get(
        `${API}/creator/titles/${manuscriptId}/outline`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('[OutlineEditor] Loaded outline:', {
        source: response.data.outline?.source,
        segmentCount: response.data.outline?.segments?.length
      });
      
      const loadedSegments = response.data.outline?.segments || [];
      setSegments(loadedSegments);
      setOriginalSegments(JSON.parse(JSON.stringify(loadedSegments)));
      setHasChanges(false);
      setEditingSegmentId(null);
    } catch (error) {
      console.error('[OutlineEditor] Load error:', error);
      const errorMsg = error.response?.data?.detail || 
                       error.response?.data?.error?.message ||
                       'Failed to load outline';
      setLoadError(errorMsg);
      notifyError(errorMsg);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTitleChange = useCallback((segmentId, newTitle) => {
    setSegments(prev => prev.map(seg => 
      seg.segment_id === segmentId ? { ...seg, title: newTitle } : seg
    ));
    setHasChanges(true);
  }, []);
  
  const handleTextChange = useCallback((segmentId, newText) => {
    setSegments(prev => prev.map(seg => 
      seg.segment_id === segmentId ? { 
        ...seg, 
        text: newText,
        word_count: newText.split(/\s+/).filter(w => w).length,
        char_count: newText.length
      } : seg
    ));
    setHasChanges(true);
  }, []);
  
  const handleMoveUp = useCallback((index) => {
    if (index <= 0) return;
    setSegments(prev => {
      const newSegments = [...prev];
      [newSegments[index - 1], newSegments[index]] = [newSegments[index], newSegments[index - 1]];
      return newSegments.map((seg, idx) => ({ ...seg, order_index: idx }));
    });
    setHasChanges(true);
  }, []);
  
  const handleMoveDown = useCallback((index) => {
    setSegments(prev => {
      if (index >= prev.length - 1) return prev;
      const newSegments = [...prev];
      [newSegments[index], newSegments[index + 1]] = [newSegments[index + 1], newSegments[index]];
      return newSegments.map((seg, idx) => ({ ...seg, order_index: idx }));
    });
    setHasChanges(true);
  }, []);
  
  const handleEditToggle = useCallback((segmentId) => {
    setEditingSegmentId(prev => prev === segmentId ? null : segmentId);
  }, []);
  
  const handleSaveAndContinue = async () => {
    setSaving(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // Prepare payload with all segments
      const payload = {
        segments: segments.map((seg, idx) => ({
          segment_id: seg.segment_id,
          order_index: idx,
          title: seg.title,
          text: seg.text  // Always include text for hash comparison
        })),
        intent: "FULL_EDIT"
      };
      
      console.log('[OutlineEditor] Saving outline:', {
        segmentCount: payload.segments.length,
        manuscriptId
      });
      
      const response = await axios.post(
        `${API}/creator/titles/${manuscriptId}/outline/save-and-continue`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      // Log FULL response for debugging - no more "Object" mystery
      console.log('[OutlineEditor] Save response (FULL):', JSON.stringify(result, null, 2));
      
      // Detect contradiction: dirty segments exist but skip_tts=true
      const dirtyCount = result.diff?.dirty_segment_ids?.length || 0;
      const hasContradiction = dirtyCount > 0 && result.skip_tts === true;
      
      if (hasContradiction) {
        console.error('[OutlineEditor] CONTRADICTION: dirty_segment_ids has', dirtyCount, 
                      'segments but skip_tts=true! This is a backend bug.');
      }
      
      if (result.ok) {
        // Log job details for debugging
        console.log('[OutlineEditor] Job started:', {
          job_id: result.job_id,
          revision_id: result.revision_id,
          skip_tts: result.skip_tts,
          dirty_count: dirtyCount,
          dirty_segment_ids: result.diff?.dirty_segment_ids
        });
        
        // Show success message with details
        if (result.skip_tts) {
          notifySuccess('Outline updated! No audio regeneration needed.');
        } else {
          notifySuccess(`Outline updated! Audio regenerating for ${dirtyCount} segment(s)...`);
        }
        
        // Close modal immediately - processing happens in background
        setHasChanges(false);
        if (onComplete) {
          onComplete();
        }
        onClose();
      } else {
        throw new Error('Save returned ok=false');
      }
    } catch (error) {
      console.error('[OutlineEditor] Save error:', error);
      
      if (error.response?.status === 409) {
        notifyError('Another outline update is already in progress');
      } else {
        const errorMsg = error.response?.data?.detail || 
                         error.response?.data?.error?.message ||
                         'Failed to save outline';
        notifyError(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };
  
  const handleCompletionClose = () => {
    setShowCompletion(false);
    setHasChanges(false);
    // Reload to show persisted changes
    loadOutline();
    // Notify parent to refresh
    if (onComplete) {
      onComplete();
    }
  };
  
  const handleFailureClose = () => {
    setShowFailure(false);
    setFailureError(null);
  };
  
  const handleClose = () => {
    if (isProcessing) {
      // Don't allow close during processing
      return;
    }
    onClose();
  };
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent 
          className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[85vh] flex flex-col p-0"
          onPointerDownOutside={(e) => isProcessing && e.preventDefault()}
        >
          {/* Fixed Header */}
          <DialogHeader className="px-6 py-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold">Edit Outline</DialogTitle>
              {hasChanges && !isProcessing && (
                <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">
                  Unsaved changes
                </span>
              )}
            </div>
            <DialogDescription className="text-slate-400 text-sm">
              Edit segment titles and text. Changes to text will trigger audio regeneration.
            </DialogDescription>
          </DialogHeader>
          
          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
              </div>
            ) : loadError ? (
              <div className="text-center py-12">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <p className="text-red-400 mb-4">{loadError}</p>
                <Button variant="outline" onClick={loadOutline} className="border-slate-600">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            ) : isProcessing && currentJob ? (
              <div className="py-4">
                <RegeneratingModal job={currentJob} stalledWarning={stalledWarning} />
              </div>
            ) : segments.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No segments found in this outline.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {segments.map((segment, index) => (
                  <SegmentCard
                    key={segment.segment_id}
                    segment={segment}
                    index={index}
                    totalCount={segments.length}
                    onTitleChange={handleTitleChange}
                    onTextChange={handleTextChange}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    isEditing={editingSegmentId === segment.segment_id}
                    onEditToggle={handleEditToggle}
                    disabled={saving || isProcessing}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Fixed Footer */}
          <DialogFooter className="px-6 py-4 border-t border-slate-700 flex-shrink-0 flex justify-between">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isProcessing}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={loadOutline}
                disabled={saving || isProcessing || loading}
                className="border-slate-600"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Reset
              </Button>
              <Button
                onClick={handleSaveAndContinue}
                disabled={!hasChanges || saving || isProcessing || loading}
                className="bg-purple-600 hover:bg-purple-500"
                data-testid="save-outline-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save and Continue
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Completion Modal */}
      <CompletionModal
        isOpen={showCompletion}
        onClose={handleCompletionClose}
        job={currentJob}
        onPreview={() => {
          // Navigate to player with cache bust
          const revisionId = currentJob?.revision_id || Date.now();
          window.location.href = `/listen/${manuscriptId}?v=${revisionId}`;
        }}
        playbackReady={playbackReady}
      />
      
      {/* Failure Modal */}
      <FailureModal
        isOpen={showFailure}
        onClose={handleFailureClose}
        job={currentJob}
        onRetry={() => {
          // Clear failure state and try save again
          setShowFailure(false);
          handleSaveAndContinue();
        }}
      />
    </>
  );
};

export default OutlineEditorModal;
